import { useEffect, useRef, useState } from 'react';
import { useCall } from '../context/CallContext';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, RefreshCw, ShieldCheck } from 'lucide-react';

export function CallOverlay() {
  const {
    callState, callType, remoteUser, duration,
    localStream, remoteStream, isMuted, isVideoOff,
    cameraFacing, networkQuality, answerCall, declineCall, hangupCall,
    toggleMute, toggleVideo, flipCamera
  } = useCall();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);

  // PIP Draggable state
  const [pipPos, setPipPos] = useState({ x: null, y: null });
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  // Attach media streams to audio & video elements
  useEffect(() => {
    if (remoteAudioRef.current && remoteStream) {
      remoteAudioRef.current.srcObject = remoteStream;
      remoteAudioRef.current.play().catch(err => {
        console.warn('[CallOverlay] Remote audio autoplay error:', err);
      });
    }
  }, [remoteStream, callState]);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, callState]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
      remoteVideoRef.current.play().catch(err => {
        console.warn('[CallOverlay] Remote video play error:', err);
      });
    }
  }, [remoteStream, callState]);

  // Touch Drag Handlers for PIP Thumbnail
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    isDraggingRef.current = true;
    const rect = e.currentTarget.getBoundingClientRect();
    dragStartRef.current = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
  };

  const handleTouchMove = (e) => {
    if (!isDraggingRef.current) return;
    const touch = e.touches[0];
    const newX = Math.max(10, Math.min(window.innerWidth - 110, touch.clientX - dragStartRef.current.x));
    const newY = Math.max(10, Math.min(window.innerHeight - 160, touch.clientY - dragStartRef.current.y));
    setPipPos({ x: newX, y: newY });
  };

  const handleTouchEnd = () => {
    isDraggingRef.current = false;
  };

  // Mouse Drag Handlers for Desktop Browsers
  const handleMouseDown = (e) => {
    isDraggingRef.current = true;
    const rect = e.currentTarget.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };

    const handleMouseMove = (ev) => {
      if (!isDraggingRef.current) return;
      const newX = Math.max(10, Math.min(window.innerWidth - 110, ev.clientX - dragStartRef.current.x));
      const newY = Math.max(10, Math.min(window.innerHeight - 160, ev.clientY - dragStartRef.current.y));
      setPipPos({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  if (callState === 'idle') return null;

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const imageRoot = import.meta.env.VITE_IMAGE_ROOT || 'https://shopaxo.in';
  const callerAvatarUrl = remoteUser?.avatar ? (remoteUser.avatar.startsWith('http') ? remoteUser.avatar : imageRoot + remoteUser.avatar) : null;
  const initial = remoteUser?.name ? remoteUser.name[0].toUpperCase() : 'U';

  return (
    <div className="call-overlay-backdrop">
      <style>{`
        .call-overlay-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: #020617;
          display: flex;
          flex-direction: column;
          font-family: system-ui, -apple-system, sans-serif;
          color: #fff;
          padding: 0;
          margin: 0;
          box-sizing: border-box;
          overflow: hidden;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
        }
        .remote-video-el {
          object-fit: cover !important;
          width: 100% !important;
          height: 100% !important;
          background: #000 !important;
        }
        .call-action-btn {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .call-action-btn:hover {
          transform: scale(1.08);
          filter: brightness(1.15);
        }
        .call-action-btn:active {
          transform: scale(0.95);
        }
      `}</style>

      {/* Hidden Audio element for remote WebRTC voice audio stream */}
      <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />

      {/* ── 1. Floating Top Header / Call Details ── */}
      <div style={{
        position: 'fixed',
        top: 'calc(16px + env(safe-area-inset-top, 0px))',
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        zIndex: 10000,
        pointerEvents: 'none'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '12px',
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(12px)',
          padding: '5px 14px',
          borderRadius: '20px',
          border: '1px solid rgba(255,255,255,0.15)',
          pointerEvents: 'auto'
        }}>
          <ShieldCheck size={14} color="#10B981" />
          <span>End-to-End Encrypted {callType} Call</span>
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: 700, margin: '4px 0 0 0', color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
          {remoteUser?.name || 'User'}
        </h2>
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', fontWeight: 500, textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
          {callState === 'outgoing' && 'Calling...'}
          {callState === 'incoming' && `Incoming ${callType} Call`}
          {callState === 'connecting' && 'Connecting...'}
          {callState === 'connected' && formatDuration(duration)}
          {callState === 'ended' && 'Call Ended'}
        </div>
        {callState === 'connected' && (
          <div style={{ fontSize: '11px', color: '#10B981', background: 'rgba(16, 185, 129, 0.2)', padding: '2px 10px', borderRadius: '10px', backdropFilter: 'blur(8px)' }}>
            ● {networkQuality} Connection
          </div>
        )}
      </div>

      {/* ── 2. Full-Screen Center Media Stage ── */}
      <div style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'hidden' }}>
        {callType === 'Video' && callState === 'connected' ? (
          /* Video Call Active Stage */
          <div style={{ position: 'relative', width: '100%', height: '100%', background: '#000' }}>
            {/* Remote Video Track */}
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              muted
              className="remote-video-el"
            />

            {/* Draggable PIP Local Video Frame */}
            <div
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
              style={{
                position: 'fixed',
                left: pipPos.x !== null ? `${pipPos.x}px` : 'auto',
                top: pipPos.y !== null ? `${pipPos.y}px` : 'auto',
                bottom: pipPos.y === null ? 'calc(100px + env(safe-area-inset-bottom, 0px))' : 'auto',
                right: pipPos.x === null ? '16px' : 'auto',
                width: '105px',
                height: '145px',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0,0,0,0.7)',
                border: '2px solid rgba(255,255,255,0.4)',
                background: '#1e293b',
                zIndex: 10000,
                touchAction: 'none',
                cursor: 'grab'
              }}
            >
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: cameraFacing === 'user' ? 'scaleX(-1)' : 'scaleX(1)',
                  pointerEvents: 'none'
                }}
              />
            </div>
          </div>
        ) : (
          /* Voice Call or Outgoing/Incoming Avatar View */
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)' }}>
            <div style={{
              position: 'relative',
              width: '140px',
              height: '140px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: callState === 'outgoing' || callState === 'incoming' ? '0 0 40px rgba(99, 102, 241, 0.6)' : '0 10px 30px rgba(0,0,0,0.5)',
              border: '4px solid rgba(255,255,255,0.25)'
            }}>
              {callerAvatarUrl ? (
                <img src={callerAvatarUrl} alt={remoteUser?.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: '56px', fontWeight: 700, color: '#fff' }}>{initial}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Floating Bottom Call Controls Toolbar ── */}
      <div style={{
        position: 'fixed',
        bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '12px 24px',
        borderRadius: '50px',
        background: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 12px 36px rgba(0, 0, 0, 0.6)',
        zIndex: 10001,
        width: 'max-content'
      }}>

        {/* Incoming Call Accept/Reject Buttons */}
        {callState === 'incoming' ? (
          <>
            <button
              className="call-action-btn"
              onClick={declineCall}
              title="Decline Call"
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#EF4444',
                border: 'none',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(239, 68, 68, 0.4)'
              }}
            >
              <PhoneOff size={26} />
            </button>

            <button
              className="call-action-btn"
              onClick={answerCall}
              title="Answer Call"
              style={{
                width: '68px',
                height: '68px',
                borderRadius: '50%',
                background: '#10B981',
                border: 'none',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 8px 28px rgba(16, 185, 129, 0.5)'
              }}
            >
              <Phone size={30} />
            </button>
          </>
        ) : (
          /* Active or Outgoing Call Action Toolbar */
          <>
            {/* Mute Mic */}
            <button
              className="call-action-btn"
              onClick={toggleMute}
              title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: isMuted ? '#EF4444' : 'rgba(255,255,255,0.15)',
                border: 'none',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              {isMuted ? <MicOff size={22} /> : <Mic size={22} />}
            </button>

            {/* Video Disable Toggle (For Video Calls) */}
            {callType === 'Video' && (
              <button
                className="call-action-btn"
                onClick={toggleVideo}
                title={isVideoOff ? 'Turn On Camera' : 'Turn Off Camera'}
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: isVideoOff ? '#EF4444' : 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                {isVideoOff ? <VideoOff size={22} /> : <Video size={22} />}
              </button>
            )}

            {/* Flip Camera (For Video Calls) */}
            {callType === 'Video' && (
              <button
                className="call-action-btn"
                onClick={flipCamera}
                title="Switch Camera"
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <RefreshCw size={22} />
              </button>
            )}

            {/* End Call Button */}
            <button
              className="call-action-btn"
              onClick={hangupCall}
              title="End Call"
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#EF4444',
                border: 'none',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(239, 68, 68, 0.4)'
              }}
            >
              <PhoneOff size={26} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
